/**
 * Debug Texture Plane Widget
 *
 * A widget for the debug overlay that shows all spawned debug texture planes
 * organized by type (like layers), with opacity controls and delete buttons.
 */

import { DebugWidget, createWidgetContainer } from './DebugOverlay';
import { DebugTexturePlaneManager } from './DebugTexturePlane';

export class DebugTexturePlaneWidget implements DebugWidget {
  name = 'debug-texture-planes';
  container: HTMLElement;
  private planeManager: DebugTexturePlaneManager;
  private layersContainer: HTMLElement;
  private lastTypeCount = -1;
  private lastTypes = '';

  constructor(planeManager: DebugTexturePlaneManager) {
    this.planeManager = planeManager;
    this.container = createWidgetContainer('🖼️ Debug Planes');

    // Create layers container
    this.layersContainer = document.createElement('div');
    this.layersContainer.style.cssText = `
      display: flex;
      flex-direction: column;
      gap: 8px;
    `;
    this.container.appendChild(this.layersContainer);

    // Initial render
    this.update();
  }

  update(): void {
    const types = this.planeManager.getTypes();

    // Only rebuild UI if the types/counts have changed
    // This prevents slider drag from being interrupted
    const typesKey = types.map((t) => `${t.type}:${t.count}`).join(',');
    if (typesKey === this.lastTypes) {
      return; // No change, skip rebuild
    }
    this.lastTypes = typesKey;

    // Clear existing content
    this.layersContainer.innerHTML = '';

    if (types.length === 0) {
      const emptyMessage = document.createElement('div');
      emptyMessage.style.cssText = `
        color: rgba(255, 255, 255, 0.5);
        font-size: 11px;
        font-style: italic;
        padding: 8px;
        text-align: center;
      `;
      emptyMessage.textContent = 'No debug planes spawned';
      this.layersContainer.appendChild(emptyMessage);
      return;
    }

    // Add clear all button at top
    const clearAllRow = document.createElement('div');
    clearAllRow.style.cssText = `
      display: flex;
      justify-content: flex-end;
      margin-bottom: 4px;
    `;
    const clearAllBtn = document.createElement('button');
    clearAllBtn.textContent = '🗑️ Clear All';
    clearAllBtn.style.cssText = `
      background: #8b0000;
      border: none;
      color: #fff;
      padding: 4px 10px;
      border-radius: 4px;
      cursor: pointer;
      font-size: 10px;
    `;
    clearAllBtn.addEventListener('click', () => {
      this.planeManager.clearAll();
      this.update();
    });
    clearAllRow.appendChild(clearAllBtn);
    this.layersContainer.appendChild(clearAllRow);

    // Create a row for each type
    for (const { type, count, opacity } of types) {
      const row = this.createTypeRow(type, count, opacity);
      this.layersContainer.appendChild(row);
    }
  }

  private createTypeRow(type: string, count: number, opacity: number): HTMLElement {
    const row = document.createElement('div');
    row.style.cssText = `
      display: flex;
      flex-direction: column;
      padding: 8px;
      background: rgba(255, 255, 255, 0.05);
      border-radius: 4px;
      border: 1px solid rgba(255, 255, 255, 0.1);
    `;

    // Header with type name and count
    const header = document.createElement('div');
    header.style.cssText = `
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 6px;
    `;

    const typeLabel = document.createElement('span');
    typeLabel.style.cssText = `
      font-weight: bold;
      font-size: 12px;
      color: #4CAF50;
    `;
    typeLabel.textContent = this.formatTypeName(type);
    header.appendChild(typeLabel);

    const countBadge = document.createElement('span');
    countBadge.style.cssText = `
      background: rgba(255, 255, 255, 0.2);
      padding: 2px 6px;
      border-radius: 10px;
      font-size: 10px;
      color: rgba(255, 255, 255, 0.8);
    `;
    countBadge.textContent = `${count} plane${count > 1 ? 's' : ''}`;
    header.appendChild(countBadge);

    row.appendChild(header);

    // Opacity slider row
    const opacityRow = document.createElement('div');
    opacityRow.style.cssText = `
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 6px;
    `;

    const opacityLabel = document.createElement('span');
    opacityLabel.style.cssText = `
      font-size: 11px;
      color: rgba(255, 255, 255, 0.7);
      min-width: 60px;
    `;
    opacityLabel.textContent = `${Math.round(opacity * 100)}%`;

    const opacitySlider = document.createElement('input');
    opacitySlider.type = 'range';
    opacitySlider.min = '0';
    opacitySlider.max = '100';
    opacitySlider.value = String(Math.round(opacity * 100));
    opacitySlider.style.cssText = `
      flex: 1;
      cursor: pointer;
      height: 4px;
    `;
    opacitySlider.addEventListener('input', () => {
      const value = parseInt(opacitySlider.value) / 100;
      this.planeManager.setTypeOpacity(type, value);
      opacityLabel.textContent = `${opacitySlider.value}%`;
    });

    opacityRow.appendChild(opacitySlider);
    opacityRow.appendChild(opacityLabel);
    row.appendChild(opacityRow);

    // Delete button
    const deleteBtn = document.createElement('button');
    deleteBtn.textContent = '🗑️ Delete All';
    deleteBtn.style.cssText = `
      background: #c0392b;
      border: none;
      color: #fff;
      padding: 4px 8px;
      border-radius: 4px;
      cursor: pointer;
      font-size: 10px;
      align-self: flex-start;
    `;
    deleteBtn.addEventListener('click', () => {
      this.planeManager.clearPlanesByType(type);
      this.update();
    });
    row.appendChild(deleteBtn);

    return row;
  }

  private formatTypeName(type: string): string {
    // Convert kebab-case to Title Case
    return type
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  destroy(): void {
    // Nothing to clean up
  }
}
